import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import NoteIcon from '@mui/icons-material/StickyNote2';
import ManageSessionDialog, { SessionData } from '../components/ManageSessionDialog';

export default function ActiveSessions() {
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState<SessionData | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchActiveSessions = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'parkingsessions'),
        where('sessionActive', '==', true)
      );
      const querySnapshot = await getDocs(q);
      const sessionsData: SessionData[] = querySnapshot.docs.map(doc => {
        const data = doc.data();

        const startTs = data.startTime;
        const startDateObj =
          startTs && typeof startTs.toDate === 'function'
            ? startTs.toDate()
            : null;

        return {
          id: doc.id,
          sessionID: doc.id,
          userEmail: data.customerInfo?.email ?? '',
          lotName: data.lotName ?? '',
          lotID: data.lotID ?? '',
          spaceNumber: data.spaceNumber ?? '',
          vehicle: data.vehicleInfo
            ? {
                licensePlate: data.vehicleInfo.licensePlate ?? '',
                make: data.vehicleInfo.make ?? '',
                color: data.vehicleInfo.color ?? '',
                nickname: data.vehicleInfo.nickname ?? '',
              }
            : undefined,
          duration: data.duration ?? undefined,
          prepaidHours: data.duration
            ? Math.ceil(data.duration / 60)
            : undefined,
          currency: data.currency ?? '',
          rate: data.rate ?? undefined,
          adminNotes: data.adminNotes ?? [],
          sessionActive: data.sessionActive ?? false,
          startDate: startDateObj
            ? startDateObj.toLocaleDateString()
            : '',
          startTime: startDateObj
            ? startDateObj.toLocaleTimeString()
            : '',
        };
      });

      setSessions(sessionsData);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveSessions();
    const interval = setInterval(fetchActiveSessions, 30000);
    return () => clearInterval(interval);
  }, []);

  const getTimeStatus = (session: SessionData) => {
    if (!session.startDate || !session.startTime || !session.prepaidHours) return null;
    try {
      const startDateTime = new Date(`${session.startDate} ${session.startTime}`);
      const endDateTime = new Date(startDateTime.getTime() + (session.prepaidHours * 60 * 60 * 1000));
      const diffMs = endDateTime.getTime() - Date.now();
      const diffMinutes = Math.round(diffMs / (1000 * 60));
      if (diffMinutes > 0) {
        const h = Math.floor(diffMinutes / 60);
        const m = diffMinutes % 60;
        return { label: h > 0 ? `${h}h ${m}m` : `${m}m`, color: 'success' as const };
      } else {
        const over = Math.abs(diffMinutes);
        const h = Math.floor(over / 60);
        const m = over % 60;
        return { label: h > 0 ? `-${h}h ${m}m` : `-${m}m`, color: 'error' as const };
      }
    } catch {
      return null;
    }
  };

  if (loading && sessions.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 1,
        mb: 2
      }}>
        <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Active Parking Sessions
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip label={`${sessions.length} Active`} color="success" />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchActiveSessions}
            size="small"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow>
                <TableCell>Vehicle</TableCell>
                <TableCell>User Email</TableCell>
                <TableCell>Parking Lot</TableCell>
                <TableCell>Space</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Prepaid</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No active sessions
                  </TableCell>
                </TableRow>
              ) : (
                sessions.map((session) => {
                  const status = getTimeStatus(session);
                  const hasNotes = session.adminNotes && session.adminNotes.length > 0;
                  return (
                    <React.Fragment key={session.id}>
                      <TableRow
                        sx={{
                          backgroundColor: status?.color === 'error' ? '#ffebee' : 'inherit',
                          '& > td': hasNotes ? { borderBottom: 'none' } : {},
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {session.vehicle?.licensePlate || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {session.vehicle ? `${session.vehicle.make} - ${session.vehicle.color}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{session.userEmail}</TableCell>
                        <TableCell>{session.lotName}</TableCell>
                        <TableCell>{session.spaceNumber || '—'}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{session.startDate}</Typography>
                          <Typography variant="caption" color="text.secondary">{session.startTime}</Typography>
                        </TableCell>
                        <TableCell>{session.prepaidHours ? `${session.prepaidHours}h` : 'N/A'}</TableCell>
                        <TableCell>
                          {status ? (
                            <Chip label={status.label} color={status.color} size="small" />
                          ) : (
                            <Chip label="Active" color="info" size="small" />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Manage session">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedSession(session);
                                setDialogOpen(true);
                              }}
                            >
                              <SettingsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      {hasNotes && (
                        <TableRow sx={{ backgroundColor: status?.color === 'error' ? '#ffebee' : 'inherit' }}>
                          <TableCell colSpan={8} sx={{ pt: 0, pb: 1.5 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 1 }}>
                              {session.adminNotes!.map((n, i) => {
                                const ts = n.timestamp?.toDate ? n.timestamp.toDate() : (n.timestamp?.seconds ? new Date(n.timestamp.seconds * 1000) : null);
                                return (
                                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                    <NoteIcon sx={{ fontSize: 14, color: '#90a4ae', mt: '2px' }} />
                                    <Typography variant="caption" color="text.secondary">
                                      <strong>{n.addedBy}</strong>
                                      {ts ? ` · ${ts.toLocaleDateString()} ${ts.toLocaleTimeString()}` : ''}
                                      {' — '}
                                    </Typography>
                                    <Typography variant="caption">{n.note}</Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <ManageSessionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        session={selectedSession}
        onSuccess={fetchActiveSessions}
      />
    </Box>
  );
}
